import { css, html, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { property } from "lit/decorators/property.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { idToMediaKind, isModel, Media, MediaKind, MediaTransformationKind, Ref, takeId } from "shared";
import { ICON_DOWNLOAD, ICON_EDIT, ICON_TRASH } from "../../icons";
import { asyncModel } from "../util/async-model-directive";
import { mediaKindToIcon, mediaUrl } from "../util/media-util";
import { BsCard } from "./card";
import "./progress-spinner";
import { openSketchEditor } from "../dialogs/open-sketch-editor";
import { teamsStore } from "../state/teams/teams-store";

export type MediaOption = "download" | "delete" | "edit-sketch";

@customElement("bs-media")
export class BsMedia extends BsCard {
	static get styles() {
		return [
			...super.styles,
			css`
				:host {
					overflow: hidden;
					border-radius: var(--media-border-radius, var(--border-radius-m));
					background: var(--media-background, var(--shade-light));
					position: relative;
					user-select: none;
					-webkit-user-select: none;
				}

				:host([zoomable]) #options {
					top: var(--space-xs);
					right: var(--space-xs);
					--card-group-height: 32px;
					--icon-size: 14px;
					--card-group-item-padding: var(--space-s);
				}

				:host(:not([loading])) #media {
					background: var(--media-background, var(--shade-lighter));
				}

				:host(:hover) #options {
					display: flex;
				}

				:host([zoomable]) #media-container {
					cursor: var(--media-zoomable-cursor, zoom-in);
				}

				:host([zoomable]) #media {
					pointer-events: none;
				}

				#media-container {
					display: flex;
					align-items: center;
					justify-content: center;
				}

				img {
					object-fit: var(--media-object-fit, contain);
				}

				#media,
				#media-container {
					height: var(--media-height, 100%);
					width: var(--media-width, 100%);
					min-height: var(--media-min-height, 100%);
					min-width: var(--media-min-width, 100%);
				}

				#media {
					max-width: 100%;
					max-height: 100%;
					border: none;
					position: relative;
				}

				#options {
					display: none;
					position: absolute;
					top: var(--space-m);
					right: var(--space-m);
				}

				#empty-container,
				#spinner {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
				}

				#empty-container {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: var(--space-xs);
					width: 80%;
				}

				#icon {
					--icon-size: 50%;
					color: var(--media-icon-color, var(--shade-normal));
				}

				#title {
					white-space: nowrap;
					max-width: 100%;
					width: 100%;
					text-align: center;
					overflow: hidden;
					text-overflow: ellipsis;
				}

				.temp-icon {
					display: flex;
					flex-direction: column;
					gap: var(--space-s);
					align-items: center;
					justify-content: center;
					position: relative;
					background: var(--shade-light);
					color: var(--shade-contrast-light);
					--icon-size: 100px;
				}

				@media (any-hover: none) {
					#options {
						display: flex;
					}
				}
			`
		];
	}

	@property({ type: String, attribute: "transformation-kind" }) transformationKind: MediaTransformationKind = "normal";
	@property({ type: Boolean, reflect: true }) zoomable = false;
	@property({ type: Array }) options?: MediaOption[];
	@property({ type: Object }) media!: Ref<Media>;
	@property({ type: Boolean, reflect: true }) loading = true;

	protected updated(props: PropertyValues) {
		super.updated(props);

		if (props.has("media")) {
			this.loading = true;
		}
	}

	download() {
		const url = mediaUrl(this.media, "original");
		window.open(url, "_blank");
	}

	async edit() {
		switch (idToMediaKind(takeId(this.media))) {
			case MediaKind.IMAGE:
				const sketch = await openSketchEditor(this.media);
				if (sketch != null) {
					this.dispatchEvent(new CustomEvent("update", { detail: sketch }));
				}
				break;
			default:
				// Not supported yet
				break;
		}
	}

	delete() {
		this.dispatchEvent(new CustomEvent("delete"));
	}

	zoom() {
		if (this.zoomable) {
			this.dispatchEvent(new CustomEvent("zoom"));
		}
	}

	onLoaded() {
		this.loading = false;
	}

	onError(e: ErrorEvent) {
		this.loading = false;
		// TODO: Show error somehow?
		console.error("error", e);
	}

	renderMediaOptions() {
		const { media } = this;
		if (this.options == null || this.options.length === 0) return;
		return html`
			<bs-card-group id="options">
				${repeat(this.options, (option) => {
					switch (option) {
						case "edit-sketch":
							if (idToMediaKind(takeId(media)) !== MediaKind.IMAGE) return undefined;
							return html`
								<bs-card-group-item>
									<bs-icon button .icon="${ICON_EDIT}" @click="${this.edit}"></bs-icon>
								</bs-card-group-item>
							`;
						case "download":
							return html`
								<bs-card-group-item>
									<bs-icon button .icon="${ICON_DOWNLOAD}" @click="${this.download}"></bs-icon>
								</bs-card-group-item>
							`;
						case "delete":
							return html`
								<bs-card-group-item>
									<bs-icon button warn .icon="${ICON_TRASH}" @click="${this.delete}"></bs-icon>
								</bs-card-group-item>
							`;
					}
				})}
			</bs-card-group>
		`;
	}

	renderMediaTitle(media: Ref<Media>): TemplateResult | undefined {
		if (isModel(media) && media.title?.length) {
			return html`<bs-text id="title" size="small">${media.title}</bs-text>`;
		}

		return undefined;
	}

	render() {
		const { media, transformationKind, zoomable, loading } = this;
		const kind = idToMediaKind(takeId(media));

		// In microsoft teams the sandbox property "allow-plugins" is not activated. Therefore do not render the PDF.
		const isSandboxedWithoutPlugins = teamsStore.initialized;
		const onlyShowIcon = kind === MediaKind.OTHER || (isSandboxedWithoutPlugins && kind === MediaKind.DOCUMENT);

		return html` <div id="media-container" @click="${this.zoom}">
				${when(
					loading && !onlyShowIcon,
					() => html`<bs-progress-spinner id="spinner"></bs-progress-spinner>`,
					() => html`
						<div id="empty-container">
							<bs-icon id="icon" .icon="${mediaKindToIcon(kind)}"></bs-icon>
							${when(
								(kind == null || onlyShowIcon) && !isModel(media),
								() => html` ${asyncModel(media, (media) => this.renderMediaTitle(media))} `,
								() => this.renderMediaTitle(media)
							)}
						</div>
					`
				)}
				${(() => {
					switch (kind) {
						case MediaKind.IMAGE:
							return html`
								<img
									id="media"
									src="${mediaUrl(media, transformationKind)}"
									@load="${this.onLoaded}"
									@error="${this.onError}"
									loading="lazy"
								/>
							`;
						case MediaKind.VIDEO:
							return html`<video
								id="media"
								src="${mediaUrl(media, transformationKind)}"
								@loadeddata="${this.onLoaded}"
								@error="${this.onError}"
								?controls="${!zoomable}"
								?autoplay="${!zoomable}"
							/>`;
						case MediaKind.AUDIO:
							return html`<audio
								id="media"
								src="${mediaUrl(media, transformationKind)}"
								@loadeddata="${this.onLoaded}"
								@error="${this.onError}"
								?controls="${!zoomable}"
								?autoplay="${!zoomable}"
							/>`;
						case MediaKind.DOCUMENT:
							if (isSandboxedWithoutPlugins) return;

							// https://pdfobject.com/pdf/pdf_open_parameters_acro8.pdf
							// https://pdfobject.com/static/
							return html`
								<object
									id="media"
									data="${mediaUrl(media, transformationKind)}${zoomable
										? "#toolbar=0&scrollbar=0&view=fit&page=1"
										: ""}"
									type="application/pdf"
									@load="${this.onLoaded}"
									@error="${this.onError}"
								></object>
							`;
						case MediaKind.OTHER:
						default:
							return undefined;
					}
				})()}
			</div>
			${this.renderMediaOptions()}`;
	}
}
